import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { stringify } from "qs";
import CreatableSelect from "react-select/creatable";

import CustomTooltip from "@components/customTooltip";

import Checkbox from "@salesforce/design-system-react/components/checkbox";
import Icon from "@salesforce/design-system-react/components/icon";

import { AsyncPaginate, withAsyncPaginate } from "react-select-async-paginate";
import { getRequest } from "@utils/request";

import {
	ClearIndicator,
	DropdownIndicator,
	inlineDropdownStyles,
	dropdownStyles,
	Option,
	SingleValue,
	themes,
	MultiValue,
} from "@components/TinyComponents/Dropdown";

import {
	ACCOUNT_TYPE,
	FALLBACK_TAG_COLOR,
	LABEL_LIMITS,
} from "@utils/constants";
import CustomMenu from "@components/TinyComponents/SuggestionCustomComponents";
import {
	makeSelectAllLabels,
	makeSelectUserParamsForLabel,
} from "@containers/App/selectors";
import { useSelector } from "react-redux";
import { TYP_CHECKBOX, TYP_SUGGESTION } from "./ElementTypes";

const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect);

const URL = "/api/v2/params-lookup";

function SuggestionInput(props) {
	const selectRef = useRef(null);
	const [menuPlacement, setMenuPlacement] = useState("auto");
	const { value, valueLabel, suggestionProps } = props;
	const pValueObj = props.valueObj || {};
	const handleAccountType = personal => {
		const record = {
			value: personal ? ACCOUNT_TYPE.PERSONAL : ACCOUNT_TYPE.BUSINESS,
			fieldId: "F72",
			sectionId: props.sectionId,
			typeCode: TYP_CHECKBOX,
		};

		props.handleChange(record);
	};
	const users = useSelector(makeSelectUserParamsForLabel());
	const labels = useSelector(makeSelectAllLabels());

	const {
		field: { fieldId: fId },
	} = props;

	const handleChange = event => {
		const suggProps = {};
		if (props.fieldId === "F61") {
			suggProps.accountType = event?.accountType;
			suggProps.isNew = event?.__isNew__;
		}

		let record;
		/** for Email modal */
		if (props.isMulti) {
			const data = event
				? event.map((e, index) => ({
						index,
						isNew: e.__isNew__ || false,
				  }))
				: [];

			record = {
				label: event ? event.map(e => e.label) : [],
				value: event ? event.map(e => e.value) : [],
				data,
				fieldId: props.fieldId,
				fieldName: props.fieldName,
				sectionId: props.sectionId,
				typeCode: TYP_SUGGESTION,
				isRequired: props.isRequired,
				isNew: event && event.__isNew__,
				suggestionProps: suggProps,
			};
		} else {
			const label = (event && event.label) || "";
			const eventValue = (event && event.value) || "";

			const data = props.isGroup ? { isGroup: !!event?.isGroup } : {};

			record = {
				label,
				value: eventValue,
				fieldId: props.fieldId,
				fieldName: props.fieldName,
				sectionId: props.sectionId,
				typeCode: TYP_SUGGESTION,
				isRequired: props.isRequired,
				isNew: event && event.__isNew__,
				suggestionProps: suggProps,
				data,
			};
		}

		props.handleChange(record);
	};

	const handleBlur = () => {
		/**
		 * we are only supporting handleblur for "to" field in email modal currently,
		 *  this might also work for the rest need to check multiple places before removing this check verify in  filters,forms etc..
		 */
		const supportedField = props.fieldId === "to";
		if (supportedField) {
			const record = {
				fieldId: props.fieldId,
				fieldName: props.fieldName,
				sectionId: props.sectionId,
				typeCode: TYP_SUGGESTION,
				value: props.value,
				label: props.valueLabel,
				isRequired: props.isRequired,
			};
			props.handleBlur(record);
		}
	};

	const handleMenuOpen = () => {
		if (!props.isGroup && !props.autoAdjustDropdown) return;
		if (!selectRef.current?.controlRef) return;

		const bounding = selectRef.current.controlRef.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const spaceBelow = viewportHeight - bounding.bottom;

		setMenuPlacement(spaceBelow < 250 ? "top" : "bottom");
		document.body.classList.add("body-no-scroll");
	};

	const getFieldId = () => {
		if (!["LM", "CM"].includes(props.moduleId)) {
			return "ALL_EMAIL";
		}
		return props.moduleId === "LM" ? "F7" : "F48";
	};

	const loadOptions = async (search, prevOptions, additional = {}) => {
		const {
			field: { fieldId },
			moduleId,
			bypass,
		} = props;
		const query = {
			bypass,
			fieldId,
			search,
			skip: prevOptions.length,
			...additional,
		};

		if (["F171", "F172", "F173", "labels"].includes(fieldId)) {
			// Need this bcs we need to tranform the users value
			const userMap = new Map();
			users.forEach(user => {
				const uId = user.value.toString();

				userMap.set(uId, user);
			});
			let options = labels.map(label => {
				let lbUsers = label?.users || [];
				let isDisabled = false;

				if (fieldId === "labels") {
					isDisabled =
						value &&
						!value?.includes(label._id) &&
						lbUsers.length >= LABEL_LIMITS.MAX_USERS_PER_LABEL;
				}

				lbUsers = lbUsers.map(user => {
					const res = userMap.get(user);

					return res.label;
				});
				return {
					...label,
					value: label?._id.toString() || label?.id?.toString(),
					label: label.name,
					users: [...lbUsers],
					isDisabled,
				};
			});

			if (search) {
				const searchInput = search.trim().toLowerCase();
				options = options.filter(option =>
					option.label.toLowerCase().includes(searchInput),
				);
			}
			return {
				options,
				hasMore: false,
			};
		}

		if (fieldId === "F5" || fieldId === "F150") {
			query.moduleId = props.moduleId;
		}
		if (fieldId === "to" || fieldId === "cc") {
			query.fieldId = getFieldId();
			query.moduleId = moduleId;
		}

		if (fieldId === "confluencePages") {
			query.fieldId = "confluence-page";
			query.confluenceSpaceKey = props.valueObj.confluenceSpaceKey;
		}

		return (async () => {
			const {
				data: { data },
			} = await getRequest(`${URL}?${stringify(query)}`);

			if (props.fieldId === "Transfer_To") {
				data.options = data.options.filter(flt => {
					return flt.value.indexOf(props.removeUser) === -1;
				});
			}

			return data;
		})();
	};

	let valueObj = null;
	if (value) {
		if (props.isMulti) {
			if (["F171", "F172", "F173", "labels"].includes(fId)) {
				const validValues = labels
					? labels.filter(lab =>
							value.some(val => {
								const lId = lab?._id?.toString() || lab.id?.toString();

								return val === lId;
							}),
					  )
					: null;
				if (validValues) {
					valueObj = validValues.map(label => {
						const lId = label?._id.toString() || label.id.toString();

						return {
							label: label.name,
							value: lId,
							colour: label.colour,
						};
					});
				}
			} else
				valueObj = valueLabel
					? valueLabel.map((l, i) => ({ label: l, value: value[i] }))
					: null;
		} else if (["F171", "F172", "F173", "labels"].includes(fId)) {
			// This case is for when "labels" is used in "Advanced filter" where its not "isMulti"
			const validValue = labels.find(lab => {
				const lId = lab?._id?.toString() || lab.id?.toString();
				return value === lId;
			});
			valueObj = {
				label: validValue?.name || "",
				value: validValue?._id?.toString() || validValue?.id?.toString(),
				colour: validValue?.colour || FALLBACK_TAG_COLOR,
			};
		} else {
			valueObj = {
				label: valueLabel,
				value,
				...(props?.isGroup ? { isGroup: props?.valueObj?.isGroup } : {}),
			};
		}
	}

	const cacheUniqs = props.parentId
		? pValueObj[props.parentId] || undefined
		: undefined;

	return props.isCreatable ? (
		<React.Fragment>
			<CreatableAsyncPaginate
				components={{ ClearIndicator, DropdownIndicator, SingleValue, Option }}
				isDisabled={props.disable}
				placeholder={props.placeholder || "Search..."}
				defaultOptions
				isMulti={!!props.isMulti}
				value={valueObj}
				loadOptions={loadOptions}
				debounceTimeout={300}
				onChange={handleChange}
				isClearable={props.isClearable}
				sectionId={props.sectionId}
				menuShouldBlockScroll
				menuPosition="absolute"
				menuPlacement="auto"
				menuPortalTarget={!props.inline && document.body}
				className={cx(
					props.className,
					"pc-modal-select-box",
					props.disable && "pc-modal-select-box-disabled",
				)}
				theme={themes}
				styles={dropdownStyles}
				selectProps={{ ...suggestionProps }}
				id={props.fieldId}
			/>
			{props.showAccountType && suggestionProps.isNew && (
				<React.Fragment>
					<Checkbox
						labels={{
							label: (
								<span>
									Is it a Personal Account?
									<CustomTooltip desc="Personal account is an individual account, which is not associated with any company.">
										<div>
											<Icon
												category="custom"
												name="pc-info"
												size="xx-small"
												className="account-type-help-icon"
												style={{ cursor: "pointer" }}
											/>
										</div>
									</CustomTooltip>
								</span>
							),
						}}
						onChange={({ target: { checked } }) => {
							handleAccountType(checked);
						}}
						checked={props.valueObj.F72 === ACCOUNT_TYPE.PERSONAL}
						className="account-type-checkbox"
					/>
				</React.Fragment>
			)}
		</React.Fragment>
	) : (
		<AsyncPaginate
			components={{
				ClearIndicator,
				DropdownIndicator,
				SingleValue,
				Option,
				MultiValue,
				...(props.isGroup ? { Menu: CustomMenu } : {}),
				...(props.components || {}),
			}}
			isDisabled={props.disable}
			placeholder={props.placeholder || "Search..."}
			defaultOptions
			closeMenuOnScroll={e => {
				//please don't remove the starting space in the classnames below

				return (
					props?.isGroup &&
					![" pc-options-container", "pc-label-menu-container"].includes(
						e.target.className,
					)
				);
			}}
			cacheUniqs={[cacheUniqs]}
			isMulti={!!props.isMulti}
			closeMenuOnSelect={!props.isMulti}
			isSearchable={!props.disableSearch}
			value={valueObj}
			loadOptions={loadOptions}
			debounceTimeout={300}
			onChange={handleChange}
			onBlur={handleBlur}
			isClearable={props.isClearable}
			sectionId={props.sectionId}
			menuShouldBlockScroll
			menuPosition="absolute"
			selectRef={selectRef}
			onMenuOpen={handleMenuOpen}
			menuPlacement={menuPlacement}
			menuPortalTarget={!props.inline && document.body}
			className={cx(
				props.className,
				"pc-modal-select-box",
				props.disable && "pc-modal-select-box-disabled",
			)}
			theme={themes}
			styles={props.inline ? inlineDropdownStyles : dropdownStyles}
			selectProps={{
				...suggestionProps,
				labelErrorText: props.labelErrorText,
				...(props?.isGroup
					? {
							selectedGroupId: props.selectedGroupId,
							isGroup: props.isGroup,
							field: props.field,
							moduleId: props.moduleId,
							bypass: props.bypass,
					  }
					: {}),
			}}
		/>
	);
}

SuggestionInput.propTypes = {
	bypass: PropTypes.bool,
	fieldId: PropTypes.string,
	parentId: PropTypes.string,
	moduleId: PropTypes.string,
	sectionId: PropTypes.string,
	value: PropTypes.string,
	valueLabel: PropTypes.string,
	disable: PropTypes.bool,
	isRequired: PropTypes.bool,
	isClearable: PropTypes.bool,
	inline: PropTypes.bool,
	className: PropTypes.string,
	handleChange: PropTypes.func,
	fieldName: PropTypes.string,
	field: PropTypes.object,
	isMulti: PropTypes.bool,
	isCreatable: PropTypes.bool,
	handleBlur: PropTypes.func,
	removeUser: PropTypes.string,
	showAccountType: PropTypes.bool,
	suggestionProps: PropTypes.object,
	valueObj: PropTypes.object,
	placeholder: PropTypes.string,
	disableSearch: PropTypes.bool,
	isGroup: PropTypes.bool,
	selectedGroupId: PropTypes.string,
	components: PropTypes.object,
	labelErrorText: PropTypes.string,
	autoAdjustDropdown: PropTypes.bool,
};

SuggestionInput.defaultProps = {
	suggestionProps: {},
};

export default SuggestionInput;
